/**
 * Heading extraction + slugification for the in-article TOC.
 *
 * Used by:
 *  - ArticleDetail / VersionHistoryDrawer to feed the sidebar's
 *    "In this article" list
 *  - ArticleBody's h1/h2/h3 renderer to assign IDs that the TOC anchors
 *    can scroll to
 *
 * Both sides must produce the same IDs for the same heading text — keep
 * them in sync by going through `slugifyHeading()` everywhere.
 */

import type { ReactNode } from "react";

export interface ArticleHeading {
  level: number; // 1 | 2 | 3
  text: string;
  id: string;
}

/**
 * `scroll-margin-top` applied to every heading the TOC scrolls to,
 * including the synthetic "Queries · Live" anchor in `ArticleDetail`.
 *
 * Sized to clear the sticky article header chrome so a TOC click lands
 * the target heading just below it. The chrome stack at scroll-locked
 * state is:
 *   - `top: 24` sticky offset (the rail's offset; the bar matches)
 *   - the `::before` shield extends the bar's chrome from y=24 up to
 *     y=0, so the visible chrome actually starts at viewport top
 *   - the bar itself is ~72px tall (two lines + padding)
 *   - = chrome occupies ~y=0 to y=96
 *
 * 104px = 96 + 8px breathing room. Centralizing the constant here
 * means body headings (rendered by `ArticleBody`) and the
 * `live-queries` anchor (rendered by `ArticleDetail`) stay in sync if
 * the header dimensions ever change.
 */
export const HEADING_SCROLL_MARGIN_TOP = "104px";

/**
 * Turn arbitrary heading text into a URL-safe slug fragment.
 * Lowercase, alphanumerics only, hyphens for separators. Mirrors the
 * common `rehype-slug` behavior so external tools that link by anchor
 * keep working if we ever add one.
 *
 *   "Executive Summary"   → "executive-summary"
 *   "  League Trends!  "  → "league-trends"
 *   "Section 3.2"         → "section-3-2"
 */
export function slugifyHeading(text: string): string {
  return (
    text
      .trim()
      .toLowerCase()
      // Replace anything non-alphanumeric (including unicode spaces) with `-`
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section"
  );
}

/**
 * Walk a markdown source string and pull out top-three-level headings.
 * Skips lines inside fenced code blocks so headings written inside code
 * examples don't end up in the TOC. Returns headings in source order.
 *
 * We intentionally stop at h3 — deeper levels would clutter the sidebar.
 */
export function extractHeadings(markdown: string): ArticleHeading[] {
  if (!markdown) return [];
  const out: ArticleHeading[] = [];
  const lines = markdown.split("\n");
  let inFence = false;
  // Track seen IDs so we can disambiguate duplicate headings with -2, -3, ...
  const seen = new Map<string, number>();

  for (const raw of lines) {
    const line = raw.trimEnd();
    // Toggle fenced code block on ``` (or ~~~) regardless of language.
    if (/^\s{0,3}(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const m = line.match(/^(#{1,3})\s+(.+?)\s*#*\s*$/);
    if (!m) continue;
    const level = m[1].length;
    const text = m[2].trim();
    if (!text) continue;
    const baseId = slugifyHeading(text);
    const n = seen.get(baseId) ?? 0;
    seen.set(baseId, n + 1);
    const id = n === 0 ? baseId : `${baseId}-${n + 1}`;
    out.push({ level, text, id });
  }
  return out;
}

/**
 * Returns a stringy heading text from React children. The h1/h2/h3
 * renderer receives mdast children that may include inline emphasis;
 * we collapse them to a flat string for slug generation. Stays in sync
 * with what `extractHeadings` saw in the source markdown.
 */
export function childrenToHeadingText(children: ReactNode): string {
  // React text nodes are strings/numbers; everything else (em, strong,
  // code) has nested children we can recurse into.
  const parts: string[] = [];
  const walk = (node: ReactNode) => {
    if (node == null || typeof node === "boolean") return;
    if (typeof node === "string" || typeof node === "number") {
      parts.push(String(node));
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    // React element: recurse into props.children if present.
    const el = node as { props?: { children?: ReactNode } };
    if (el?.props?.children !== undefined) walk(el.props.children);
  };
  walk(children);
  return parts.join("");
}
